/**
 * fractalDrift — Julia-set warp-field preset.
 *
 * The warp field is driven by the continuous escape count of z -> z^2 + c,
 * producing genuine fractal displacement filaments. On each beat adoption
 * the c parameter walks a small circle around the dendrite base point
 * (-0.7269, 0.1889), morphing the fractal shape without leaving the
 * interesting Mandelbrot-boundary region.
 *
 * The seed is deliberately dim and narrow so the *fractal displacement*
 * is the visual subject, not the waveform ribbon (per the algorithm
 * notes: the seed injects energy, the warp is the subject).
 */

import { WarpKind, type VisualizerPreset } from '../presetTypes';

export const fractalDriftPreset: VisualizerPreset = {
  id: 'fractalDrift',
  name: 'Fractal Drift',
  seed: {
    kind: 'waveform',
    params: {
      baseY: 0.0,
      gain: 0.35,
      halfThick: 0.010,
      intensity: 0.5,
      color: [0.6, 0.4, 0.95], // deep violet
      edgeSharp: 9.5,
    },
  },
  warp: {
    kind: WarpKind.Field,
    params: {
      baseZoom: 0.006,
      bassZoomGain: 0.025,
      rotation: 0,
      bassRotationGain: 0,
      ripple: 0,
      rippleFreq: 0,
      decay: 0.972,
      chroma: 0.003,
      hueShift: 0.01,
      beatPunch: 0.05,
      centerDriftAmp: 0.03,
      centerDriftRate: 0.09,
    },
  },
  warpField: {
    generator: 'julia',
    // Seed advances by 7.13 per adoption; the shader walks c along a
    // small circle so each beat morphs to a neighbouring Julia set.
    seed: 0.0,
    strength: 0.013,
    centerX: 0.5,
    centerY: 0.5,
    // c base point: classic dendrite Julia, reads as "fractal lightning".
    param0: -0.7269, // c.re
    param1: 0.1889,  // c.im
    param2: 1.4,     // complex-plane zoom (how much of the set fills the field)
    adoption: {
      beatThreshold: 0.6,
      cooldownMs: 2000,
      crossfadeMs: 700,
      regenerateOnAdopt: true,
    },
  },
  modulation: [
    {
      target: 'warp.baseZoom',
      source: 'bass',
      transform: 'smoothPulse',
      amount: 0.014,
      smoothingMs: 110,
    },
    {
      target: 'seed.intensity',
      source: 'rms',
      transform: 'linear',
      amount: 0.4,
      smoothingMs: 80,
    },
    {
      target: 'warp.hueShift',
      source: 'beatConfidence',
      transform: 'smoothPulse',
      amount: 0.02,
      smoothingMs: 250,
    },
  ],
  transition: { durationMs: 1000 },
  present: {
    bloomEnabled: true,
    // Fractal escape boundaries produce sharp bright filaments; a high
    // threshold catches only those so the fractal structure stays
    // readable instead of blowing out the whole frame.
    bloomThreshold: 0.8,
    bloomKnee: 0.2,
    bloomSpread: 1.2,
    bloomIntensity: 0.6,
    vignette: 0.25,
    grain: 0.01,
  },
};
